import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, RefreshCw, Check, X } from 'lucide-react';
import { Button } from './ui/button';

export default function CameraCapture({ onCapture, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (err) {
      setError('Could not access camera. Please grant permission and try again.');
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Center crop
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setPreview(dataUrl);
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const retake = () => {
    setPreview(null);
    startCamera();
  };

  const confirm = () => {
    if (!preview) return;
    // Convert data URL to Blob
    const arr = preview.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const blob = new Blob([u8arr], { type: mime });
    const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: mime });
    onCapture(file);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="aspect-square bg-slate-900 rounded-2xl overflow-hidden relative mb-6">
          {error ? (
            <div className="w-full h-full flex items-center justify-center p-6 text-center text-white">
              <div>
                <X className="w-12 h-12 mx-auto mb-3 text-red-400" />
                <p>{error}</p>
              </div>
            </div>
          ) : preview ? (
            <img src={preview} alt="Selfie preview" className="w-full h-full object-cover" />
          ) : (
            <>
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-video" />
              <div className="absolute inset-8 border-4 border-white/40 rounded-full pointer-events-none"></div>
              <p className="absolute bottom-4 left-0 right-0 text-center text-white text-sm">
                Position your face in the circle
              </p>
            </>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" />

        <div className="flex gap-3 justify-center">
          {preview ? (
            <>
              <Button data-testid="retake-btn" onClick={retake} className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-3 border border-white/30">
                <RefreshCw className="w-4 h-4 mr-2" /> Retake
              </Button>
              <Button data-testid="confirm-selfie-btn" onClick={confirm} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full py-3 font-bold">
                <Check className="w-4 h-4 mr-2" /> Use Photo
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="cancel-camera-btn" onClick={onCancel} className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-3 border border-white/30">
                Cancel
              </Button>
              <Button data-testid="take-photo-btn" onClick={takePhoto} disabled={!stream} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full py-3 font-bold">
                <Camera className="w-4 h-4 mr-2" /> Take Photo
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
